import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CardTop } from '../card-top'
import { getVisibleCount } from './get-visible-count'

import {
  CardSlot,
  CarouselWrapper,
  NavButton,
  NavSpacer,
  SectionTitle,
  Slide,
  SlideTrack,
  SlideViewport,
} from './top-games.styled'

const RENDER_BUFFER = 3

const CarouselCard = memo(function CarouselCard({
  game,
  number,
  displayNumber,
  isMounted,
  size,
  featured,
  compactAspect,
  onLaunchGame,
}) {
  return (
    <CardSlot data-card-slot>
      {isMounted ? (
        <CardTop
          game={game}
          number={number}
          displayNumber={displayNumber}
          size={size}
          featured={featured}
          compactAspect={compactAspect}
          onLaunchGame={
            onLaunchGame ? (isDemo) => onLaunchGame(game, isDemo) : undefined
          }
        />
      ) : null}
    </CardSlot>
  )
})

export const TopGames = memo(function TopGames({
  title,
  games = [],
  displayNumber = false,
  size = 'lg',
  featured,
  compactAspect = false,
  onLaunchGame,
}) {
  const viewportRef = useRef(null)
  const visibleCountRef = useRef(4)
  const [startIndex, setStartIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(4)

  const safeGames = useMemo(
    () => (Array.isArray(games) ? games.filter(Boolean) : []),
    [games]
  )

  const hasOverflow = safeGames.length > visibleCount
  const maxStartIndex = Math.max(0, safeGames.length - visibleCount)
  const canScrollLeft = hasOverflow && startIndex > 0
  const canScrollRight = hasOverflow && startIndex < maxStartIndex

  const renderWindow = useMemo(() => {
    const bufferStart = Math.max(0, startIndex - RENDER_BUFFER)
    const bufferEnd = Math.min(
      safeGames.length,
      startIndex + visibleCount + RENDER_BUFFER
    )

    return { bufferStart, bufferEnd }
  }, [safeGames.length, startIndex, visibleCount])

  const updateVisibleCount = useCallback((width) => {
    const nextVisibleCount = getVisibleCount(width, size)
    if (visibleCountRef.current === nextVisibleCount) return

    visibleCountRef.current = nextVisibleCount
    setVisibleCount(nextVisibleCount)
  }, [size])

  useEffect(() => {
    setStartIndex(0)
  }, [safeGames.length, size])

  useEffect(() => {
    if (!hasOverflow) {
      setStartIndex(0)
      return
    }

    setStartIndex((prev) => Math.min(prev, maxStartIndex))
  }, [hasOverflow, maxStartIndex])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return undefined

    let frameId = 0

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (!width) return

      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => updateVisibleCount(width))
      })
    })

    observer.observe(viewport)
    updateVisibleCount(viewport.offsetWidth)

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frameId)
    }
  }, [updateVisibleCount, size])

  const handleScrollLeft = useCallback(() => {
    setStartIndex((prev) => Math.max(prev - 1, 0))
  }, [])

  const handleScrollRight = useCallback(() => {
    setStartIndex((prev) => Math.min(prev + 1, maxStartIndex))
  }, [maxStartIndex])

  if (!safeGames.length) {
    return null
  }

  return (
    <Slide>
      {title ? <SectionTitle style={{ paddingLeft: 40 }}>{title}</SectionTitle> : null}

      <CarouselWrapper>
        {canScrollLeft ? (
          <NavButton
            type='button'
            aria-label='Ver juegos anteriores'
            onClick={handleScrollLeft}
          >
            <i className='ion-chevron-left' />
          </NavButton>
        ) : (
          <NavSpacer aria-hidden='true' />
        )}

        <SlideViewport
          ref={viewportRef}
          $visibleCount={visibleCount}
          $size={size}
          $compactAspect={compactAspect}
        >
          <SlideTrack data-slide-track $startIndex={startIndex}>
            {safeGames.map((game, index) => (
              <CarouselCard
                key={game.id ?? index}
                game={game}
                number={index + 1}
                displayNumber={displayNumber}
                size={size}
                featured={featured}
                compactAspect={compactAspect}
                onLaunchGame={onLaunchGame}
                isMounted={
                  index >= renderWindow.bufferStart && index < renderWindow.bufferEnd
                }
              />
            ))}
          </SlideTrack>
        </SlideViewport>

        {canScrollRight ? (
          <NavButton
            type='button'
            aria-label='Ver más juegos'
            onClick={handleScrollRight}
          >
            <i className='ion-chevron-right' />
          </NavButton>
        ) : (
          <NavSpacer aria-hidden='true' />
        )}
      </CarouselWrapper>
    </Slide>
  )
})